import React, { useEffect, useState } from 'react';
import { searchNft } from '../util/api';

const PAGE_SIZE = 4;
const SEARCH_DEBOUNCE_MS = 400;

const shortAddress = (address) =>
  address && address.length > 12
    ? `${address.slice(0, 4)}...${address.slice(-6)}`
    : address || '-';

/**
 * The artwork viewer on the landing page: a search box and a four-card rail
 * of minted Eyes, read page by page from /nft/search. The arrows step through
 * pages and wrap around at either end, as the original rail did.
 */
export default function EyeViewer() {
  const [input, setInput] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setQuery(input.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [input]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    searchNft(query, page, PAGE_SIZE)
      .then((res) => {
        if (cancelled) return;
        setItems(res?.data || []);
        setTotal(res?.total || 0);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error(err);
        setItems([]);
        setTotal(0);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [query, page]);

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const onPrev = () => setPage((p) => (p <= 1 ? pageCount : p - 1));
  const onNext = () => setPage((p) => (p >= pageCount ? 1 : p + 1));

  return (
    <>
      <div className="viewer-searchbar">
        {' '}
        <div className="viewer-search">
          {' '}
          <span>
            ⌕
          </span>
          {' '}
          <input
            id="eyeSearch"
            type="search"
            placeholder="Search Eye #, name, inscription or address"
            autoComplete="off"
            value={input}
            onChange={(e) => setInput(e.target.value)}
          />
          {' '}
        </div>
        {' '}
        <div className="viewer-count">
          <b>
            {total ? `${total.toLocaleString()} MINTED EYES` : 'MINTED EYES'}
          </b>
          <span>
            {pageCount > 1 ? `PAGE ${page} / ${pageCount}` : 'LIVE COLLECTION'}
          </span>
        </div>
        {' '}
      </div>
      {' '}
      <div className="viewer-carousel-shell">
        {' '}
        <button
          className="viewer-arrow viewer-prev"
          type="button"
          aria-label="Previous artworks"
          onClick={onPrev}
          disabled={pageCount <= 1}
        >
          ‹
        </button>
        {' '}
        <div
          className="viewer-grid"
          id="viewerGrid"
          style={{ opacity: loading ? 0.5 : 1, transition: 'opacity .2s' }}
        >
          {!loading && items.length === 0 && (
            <p style={{ width: '100%', textAlign: 'center', color: '#777', padding: '60px 0' }}>
              {query ? `No Eyes found for "${query}".` : 'No minted Eyes yet.'}
            </p>
          )}
          {items.map((item) => (
            <article
              className="viewer-card"
              key={item.id}
              data-token={item.id}
              data-name={(item.name || '').toLowerCase()}
            >
              {' '}
              <button className="viewer-art" type="button" aria-label={`View Eye #${item.id}`}>
                {' '}
                <img
                  src={`https://eyes4384.com/images/${item.id - 1}.webp`}
                  alt={`Eye #${item.id}`}
                  loading="lazy"
                />
                {' '}
              </button>
              {' '}
              <div className="viewer-meta">
                {' '}
                <div>
                  <span>
                    EYE #{item.id}
                  </span>
                  <b>
                    {item.name || `Eye #${item.id}`}
                  </b>
                </div>
                {' '}
              </div>
              {' '}
              <div className="viewer-details">
                {' '}
                <div>
                  <span>
                    INSCRIPTION
                  </span>
                  <a
                    href={`https://ordinals.com/inscription/${item.inscriptionId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    VIEW ON ORDINALS ↗
                  </a>
                </div>
                {' '}
                <div>
                  <span>
                    OWNER
                  </span>
                  <b title={item.receiveAddress}>
                    {shortAddress(item.receiveAddress)}
                  </b>
                </div>
                {' '}
              </div>
              {' '}
            </article>
          ))}
        </div>
        {' '}
        <button
          className="viewer-arrow viewer-next"
          type="button"
          aria-label="Next artworks"
          onClick={onNext}
          disabled={pageCount <= 1}
        >
          ›
        </button>
        {' '}
      </div>
    </>
  );
}
